import React, { FC } from "react";
import clsx from "clsx";
import makeStyles from "@material-ui/core/styles/makeStyles";
import Radio from "@material-ui/core/Radio";
import { FormControlLabel, RadioGroup } from "@material-ui/core";
import { useWizard } from "../../../../wizard/context";
import {
  ApplicationStatus,
  ApplicationStatusText,
  IApplicationStatusControlProps,
} from "../../../types";
import { queryCache } from "react-query";
import { useChangeStatusConfig } from "./config";

const useStyles = makeStyles((theme) => ({
  root: {
    border: "1px solid #DBDBDB",
    borderRadius: 16,
    height: 32,
    width: 354,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 12px",
    "& .MuiFormGroup-root": {
      flexDirection: "row",
    },
    "& .MuiFormControlLabel-root": {
      marginRight: 0,
      marginLeft: 7,
    },
    "& .MuiIconButton-root": {
      padding: 4,
    },
    "& .MuiFormControlLabel-label": {
      color: "#231F20",
      fontSize: 12,
    },
  },
  applicationLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: "#231F20",
  },
  radioRoot: {
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  icon: {
    borderRadius: "50%",
    width: 12,
    height: 12,
    boxShadow:
      "inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)",
    backgroundColor: "#f5f8fa",
    backgroundImage:
      "linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))",
    "$root.Mui-focusVisible &": {
      outline: "2px auto rgba(19,124,189,.6)",
      outlineOffset: 2,
    },
    "input:hover ~ &": {
      backgroundColor: "#ebf1f5",
    },
    "input:disabled ~ &": {
      boxShadow: "none",
      background: "rgba(206,217,224,.5)",
    },
  },
  checkedIcon: {
    boxShadow: "unset",
    backgroundImage:
      "linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))",
    "&:before": {
      display: "block",
      width: 12,
      height: 12,
      backgroundImage: "radial-gradient(#fff,#fff 28%,transparent 32%)",
      content: '""',
    },
  },
  [ApplicationStatusText[ApplicationStatus.PENDING]]: {
    backgroundColor: theme.palette.warning.main,
    "input:hover ~ &": {
      backgroundColor: theme.palette.warning.dark,
    },
  },
  [ApplicationStatusText[ApplicationStatus.APPROVED]]: {
    backgroundColor: theme.palette.success.main,
    "input:hover ~ &": {
      backgroundColor: theme.palette.success.dark,
    },
  },
  [ApplicationStatusText[ApplicationStatus.REJECTED]]: {
    backgroundColor: theme.palette.error.main,
    "input:hover ~ &": {
      backgroundColor: theme.palette.error.dark,
    },
  },
}));

const StyledRadio = ({ status, ...props }) => {
  const classes = useStyles();

  return (
    <Radio
      className={classes.radioRoot}
      disableRipple
      color="default"
      checkedIcon={
        <span
          className={clsx(classes.icon, classes.checkedIcon, classes[status])}
        />
      }
      icon={<span className={classes.icon} />}
      {...props}
    />
  );
};

export const ApplicationStatusControl: FC<IApplicationStatusControlProps> = ({
  status,
  id,
  schoolType,
}) => {
  const classes = useStyles();

  const { openWizard } = useWizard();
  const getApplicationStatusConfig = useChangeStatusConfig(id, schoolType);

  const changeStatus = async (newStatus: ApplicationStatus) => {
    if (await openWizard(getApplicationStatusConfig(newStatus))) {
      await queryCache.invalidateQueries();
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.applicationLabel}>Application Status</div>
      <RadioGroup>
        <FormControlLabel
          value={`${ApplicationStatus.PENDING}`}
          checked={status === ApplicationStatus.PENDING}
          control={
            <StyledRadio
              status={ApplicationStatusText[ApplicationStatus.PENDING]}
            />
          }
          onChange={() => changeStatus(ApplicationStatus.PENDING)}
          label="Pending"
        />
        <FormControlLabel
          value={`${ApplicationStatus.APPROVED}`}
          checked={status === ApplicationStatus.APPROVED}
          control={
            <StyledRadio
              status={ApplicationStatusText[ApplicationStatus.APPROVED]}
            />
          }
          onChange={() => changeStatus(ApplicationStatus.APPROVED)}
          label="Approved"
        />
        <FormControlLabel
          value={`${ApplicationStatus.REJECTED}`}
          checked={status === ApplicationStatus.REJECTED}
          control={
            <StyledRadio
              status={ApplicationStatusText[ApplicationStatus.REJECTED]}
            />
          }
          onChange={() => changeStatus(ApplicationStatus.REJECTED)}
          label="Rejected"
        />
      </RadioGroup>
    </div>
  );
};
